import { useState, useEffect } from 'react';
import { default as GraphemeSplitter } from 'grapheme-splitter';
import {
    isWordInWordList,
    loadGameStateFromLocalStorage,
    localeAwareUpperCase,
    MAX_CHALLENGES,
    REVEAL_TIME_MS,
    saveGameStateToLocalStorage,
    unicodeLength,
} from 'utils/wordle.utils';
import { Cell, Grid, Keyboard } from './components';
import { Link, Text } from 'components-guide';
import { notifySuccess, notifyError, notifyInfo } from 'api';
import './Wordle.css';

const Wordle = ({ solutionWord = 'BANCO' }) => {
    const solution = localeAwareUpperCase(solutionWord);
    const [currentGuess, setCurrentGuess] = useState('');
    const [isGameWon, setIsGameWon] = useState(false);
    const [currentRowClass, setCurrentRowClass] = useState('');
    const [isGameLost, setIsGameLost] = useState(false);
    const [isRevealing, setIsRevealing] = useState(false);
    const [guesses, setGuesses] = useState<string[]>(() => {
        const loaded = loadGameStateFromLocalStorage();
        if (loaded?.solution !== solution) {
            return [];
        }
        const gameWasWon = loaded.guesses.includes(solution);
        if (gameWasWon) {
            setIsGameWon(true);
        }
        if (loaded.guesses.length === MAX_CHALLENGES && !gameWasWon) {
            setIsGameLost(true);
            alert(`La palabra era ${solution}`);
        }
        return loaded.guesses;
    });

    useEffect(() => {
        saveGameStateToLocalStorage({ guesses, solution });
    }, [guesses]);

    useEffect(() => {
        if (isGameWon) {
            notifySuccess({
                title: 'Enhorabuena',
                message: '¡Has ganado!',
            })();
        }

        if (isGameLost) {
            notifyError({
                title: 'Oh no',
                message: '¡Has perdido!',
            })();
        }
    }, [isGameWon, isGameLost]);

    const onChar = (value: string) => {
        if (
            unicodeLength(`${currentGuess}${value}`) <= solution.length &&
            guesses.length < MAX_CHALLENGES &&
            !isGameWon
        ) {
            setCurrentGuess(`${currentGuess}${value}`);
        }
    };

    const onDelete = () => {
        setCurrentGuess(new GraphemeSplitter().splitGraphemes(currentGuess).slice(0, -1).join(''));
    };

    const onEnter = () => {
        if (isGameWon || isGameLost) {
            return;
        }

        if (!(unicodeLength(currentGuess) === solution.length)) {
            setCurrentRowClass('jiggle');
            notifyInfo({
                title: 'Oh no',
                message: '¡Faltan letras!',
            })();
            return setCurrentRowClass('');
        }

        if (!isWordInWordList(currentGuess)) {
            setCurrentRowClass('jiggle');
            notifyInfo({
                title: 'Oh no',
                message: 'Esa palabra no está en nuestra base de datos. Por favor, prueba con otra',
            })();
            return setCurrentRowClass('');
        }

        setIsRevealing(true);
        // turn this back off after all
        // chars have been revealed
        setTimeout(() => {
            setIsRevealing(false);
        }, REVEAL_TIME_MS * solution.length);

        const winningWord = solution === currentGuess;

        if (unicodeLength(currentGuess) === solution.length && guesses.length < MAX_CHALLENGES && !isGameWon) {
            setGuesses([...guesses, currentGuess]);
            setCurrentGuess('');

            if (winningWord) {
                return setIsGameWon(true);
            }

            if (guesses.length === MAX_CHALLENGES - 1) {
                setIsGameLost(true);
                notifyInfo({
                    title: 'Oh no',
                    message: `La palabra era ${solution}`,
                })();
            }
        }
    };

    const restartGameStateToLocalStorage = () => {
        notifyInfo({
            title: 'Reiniciado',
            message: `El juego se ha reiniciado exitosamente.`,
        })();
        setIsGameWon(false);
        setGuesses([]);
    };

    return (
        <div className="flex flex-col">
            <div className="pt-1 px-1 pb-2 md:max-w-7xl w-full mx-auto sm:px-6 lg:px-8 flex flex-col grow">
                <div className="grow">
                    <div className="flex justify-center mt-2 mb-1">
                        <Cell isRevealing={true} isCompleted={true} value="J" status="correct" />
                        <Cell value="U" />
                        <Cell value="G" />
                        <Cell value="A" />
                        <Cell value="R" />
                    </div>
                    <Text align="center">La letra J está en la palabra y en el lugar correcto.</Text>
                    <div className="flex justify-center mt-3 mb-1">
                        <Cell value="C" />
                        <Cell value="I" />
                        <Cell isRevealing={true} isCompleted={true} value="N" status="present" />
                        <Cell value="C" />
                        <Cell value="O" />
                    </div>
                    <Text align="center">La letra N está en la palabra pero en el lugar equivocado.</Text>
                    <div className="flex justify-center mt-3 mb-1">
                        <Cell value="L" />
                        <Cell value="A" />
                        <Cell value="V" />
                        <Cell isRevealing={true} isCompleted={true} value="A" status="absent" />
                        <Cell value="S" />
                    </div>
                    <Text align="center">La letra U no está en la palabra en ningún lugar.</Text>
                </div>
                <div className="pb-4 grow">
                    <Text typo="h5" align="center" weight="semi">
                        <Link onClick={restartGameStateToLocalStorage}>Reiniciar &#8634;</Link>
                    </Text>
                    <Grid
                        solution={solution}
                        guesses={guesses}
                        currentGuess={currentGuess}
                        isRevealing={isRevealing}
                        currentRowClassName={currentRowClass}
                    />
                </div>
                <Keyboard
                    onChar={onChar}
                    onDelete={onDelete}
                    onEnter={onEnter}
                    solution={solution}
                    guesses={guesses}
                    isRevealing={isRevealing}
                />
            </div>
        </div>
    );
};

export default Wordle;
